import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class ConsignmentPlansService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves page-get for consignment plans delivery.
     *
     * @datatask 1692
     * @datataskName sp_ws_p2_consignmentplansdelivery_settings
     * @param params the params to get the user-settings for.
     * @returns a promise containing the user-settings.
     */
    public async getConsignmentPlansDelivery(params: ConsignmentPlansDeliveryParams): Promise<ConsignmentPlansDeliveryGet> {
        return this.datatask.Post(1692, params);
    }

    /**
     * Gets the page load for the consignment plans consignment content page.
     *
     * @datatask 3803
     * @datataskName tt_consignment_plans_consignment_content_get
     * @param params to use for retrieving the get.
     * @returns a promise containing the page load.
     */
    public async getConsignmentPlansConsignmentContent(params: ConsignmentPlansConsignmentContentParams): Promise<ConsignmentPlansConsignmentContentGet> {
        return (await this.datatask.Post(3803, params))[0];
    }

    /**
     * Retrieves list of labourers for consignment delivery plans.
     *
     * @datatask 1790
     * @datataskName sp_ws_p2_consignmentplansdeliverylabour_list
     * @returns a promise containing list of labourers for consignment delivery plans.
     */
    public getConsignmentPlansDeliveryLabourers(): Promise<Item[]> {
        return this.datatask.Post(1790);
    }

    /**
     * Persists the given items.
     *
     * @datatask 2174
     * @datataskName sp_ws_p2_consignmentplansconsignmentcontent_temporary_quantity
     * @param params the temporary quantities to persist.
     * @returns promise containing errorcode and message.
     */
    public async saveTemporaryQuantities(params: TemporaryQuantityParams): Promise<ConsignmentPlansErrorResponse> {
        return (await this.datatask.Post(2174, params))[0];
    }

    /**
     * Prints picklist for the given parameters.
     *
     * @datatask 3144
     * @datataskName tt_picklist_silent_print
     * @param params the params to use for printing the picklist.
     * @returns a promise returning the errorcode and message if any.
     */
    public async printPickListReport(params: { order_internal_no: string; p2_printer_keyno: string }): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3144, params))[0];
    }

    /**
     * Completes the consignment plans content.
     *
     * @datatask 1712
     * @datataskName sp_ws_p2_consignmentplansconsignmentcontent_complete
     * @param params parameters to use for the completion.
     * @returns a promise returning the errorcode and message if any.
     */
    public async consignmentPlansConsignmentContentComplete(params: ConsignmentContentCompleteParams): Promise<ConsignmentPlansErrorResponse> {
        return (await this.datatask.Post(1712, params))[0];
    }

    /**
     * Resets the temporary quantities on the consignment.
     *
     * @datatask 2381
     * @datataskName sp_ws_p2_w_consignmentplansconsignmentcontent_switch
     * @param params parameters to use for getting the consignment to reset.
     * @returns a promise containing error code and message for the operation.
     */
    public async resetConsignmentPlansContentQuanitites(params: ResetConsignmentPlansContentQuantitiesParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2381, params))[0];
    }
}

export interface ConsignmentPlansDeliveryParams {
    consignment_keyno: string;
}

export interface ConsignmentPlansDeliveryGet {
    error_code: number;
    error_message: string;
    order_internal_no: string;
    delivery_button_label: string;
    portal_user_keyno: string;
    printer: string;
    reportdef_keyno: string;
    reportdef_keyno_waybill: string;
    labour_no: string;
    labour_name: string;
    stocklocation_keyno: string;
    stocklocation_name: string;
    delivery_note: string;
    delivery_information: string;
    consignment_keyno: string;
    is_last: 1 | 0;
}

export interface ConsignmentPlansConsignmentContentParams {
    consignment_keyno: string;
}

export interface ConsignmentPlansConsignmentContentGet {
    order_internal_no: string;
    delivery_button_label: string;
    portal_user_keyno: string;
    printer: string;
    reportdef_keyno: string;
    reportdef_keyno_waybill: string;
    labour_no: string;
    labour_name: string;
    stocklocation_keyno: string;
    stocklocation_name: string;
    delivery_note: string;
    delivery_information: string;
    consignment_keyno: string;
    show_print: '1' | '0';
    show_complete: '1' | '0';
}

export interface ConsignmentContentCompleteParams {
    test: string;
    delivery_note: string;
    records: ConsignmentContentCompleteItem[];
}

export interface ConsignmentContentCompleteItem extends ConsignmentLineQuantityItem {
    cancel_rest?: '1' | '0';
}

export interface TemporaryQuantityItem extends ConsignmentLineQuantityItem {
    cancel_rest: '1' | '0';
    is_selected: '1' | '0';
}

export interface TemporaryQuantityParams {
    records: TemporaryQuantityItem[];
}

export interface ConsignmentPlansErrorResponse {
    error_code: `${number}`;
    error_message: string;
}

export interface ResetConsignmentPlansContentQuantitiesParams {
    consignment_keyno: string;
}

interface ConsignmentLineQuantityItem {
    consignmentline_keyno: number;
    ordered_quantity: number;
}
